import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X, Check, AlertCircle } from 'lucide-react';

/**
 * VideoUploadManager - Componente para upload de vídeos com transcrição automática
 * 
 * Props:
 * - onVideoUploaded: callback(videoId, videoData) ao terminar transcrição
 * - onCancel: callback() para cancelar
 */
export default function VideoUploadManager({ onVideoUploaded, onCancel }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [transcriptionStatus, setTranscriptionStatus] = useState('pending');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const API_BASE = `${BACKEND_URL.replace(/\/$/, "")}/api`;
  // Compatibilidade: alguns lugares usam 'token', outros 'access_token'.
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');

  // Debug: verificar se token existe
  React.useEffect(() => {
    console.log('VideoUploadManager montado');
    console.log('Token disponível:', !!token);
    console.log('API_BASE:', API_BASE);
  }, []);

  // Validar arquivo selecionado
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/ogg', 'audio/mpeg', 'audio/wav', 'audio/m4a'];
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Formato não permitido. Use: MP4, MOV, AVI, WebM, OGG, MP3, WAV ou M4A');
      return;
    }

    if (selectedFile.size > 500 * 1024 * 1024) {
      setError('Arquivo muito grande (máximo 500MB)');
      return;
    }

    setFile(selectedFile);
    setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    setError(null);
  };

  // Fazer upload do vídeo em chunks de 5MB
  const handleUpload = async () => {
    if (!file || !title.trim()) {
      setError('Selecione um arquivo e defina um título');
      return;
    }
    if (!token) {
      setError('Token de autenticação não encontrado. Faça login novamente.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    const CHUNK_SIZE = 512 * 1024; // 512KB por chunk
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = crypto.randomUUID();

    try {
      let finalData = null;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', String(i));
        formData.append('total_chunks', String(totalChunks));
        formData.append('title', title);
        formData.append('filename', file.name);
        formData.append('file', chunk, file.name);

        const response = await fetch(`${API_BASE}/videos/upload/chunk`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Erro no chunk ${i + 1}`);
        }

        const data = await response.json();
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));

        if (data.status === 'complete') {
          finalData = data;
        }
      }

      if (!finalData?.video_id) throw new Error('Upload finalizado sem video_id');

      setVideoId(finalData.video_id);
      setTranscribing(true);
      setFile(null);
      pollTranscriptionStatus(finalData.video_id);

    } catch (err) {
      console.error('Erro no upload:', err);
      setError(err.message || 'Erro ao fazer upload');
      setUploading(false);
    }
  };

  // Polling do status de transcrição
  const pollTranscriptionStatus = async (vid) => {
    const maxAttempts = 60; // 5 minutos com 5s de intervalo
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/videos/${vid}/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Erro ao verificar status');

        const data = await response.json();
        setTranscriptionStatus(data.status);

        if (data.status === 'completed') {
          setTranscribing(false);
          setUploading(false);
          
          // Chamar callback com dados do vídeo
          if (onVideoUploaded) {
            onVideoUploaded(vid, {
              title,
              videoId: vid,
              transcription_text: data.transcription_text,
              transcription_segments: data.transcription_segments,
              status: 'ready'
            });
          }
        } else if (data.status === 'failed') {
          setTranscribing(false);
          setUploading(false);
          setError(`Transcrição falhou: ${data.error}`);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 5000); // Verificar a cada 5 segundos
        } else {
          setTranscribing(false);
          setUploading(false);
          setError('Timeout na transcrição (máximo 5 minutos)');
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          setTranscribing(false);
          setUploading(false);
          setError('Erro ao verificar transcrição');
        }
      }
    };

    checkStatus();
  };

  const modal = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 pointer-events-auto video-upload-manager-portal"
      onClick={(e) => {
        // Impedir que o clique propague para elementos abaixo (ex: modal de editar curso)
        e.stopPropagation();
        // Se clicou no fundo preto (não no modal branco), fecha este modal
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-lg max-w-md w-full pointer-events-auto z-[100000] relative text-gray-900 video-upload-manager"
        style={{ color: '#1f2937' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`.video-upload-manager, .video-upload-manager * { color: #1f2937 !important; background: white !important; }
.video-upload-manager h2 { color: #1f2937 !important; }
.video-upload-manager input::placeholder { color: #6b7280 !important; }
.video-upload-manager input, .video-upload-manager textarea { color: #1f2937 !important; background: white !important; }
.video-upload-manager .bg-blue-50 { background: #ebf8ff !important; }
`}</style>
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900" style={{ color: '#1f2937' }}>Upload de Vídeo</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
            disabled={uploading || transcribing}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título do Vídeo *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Aula 1 - Introdução"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              disabled={uploading || transcribing}
              style={{ pointerEvents: 'auto', color: '#1f2937', backgroundColor: 'white' }}
              tabIndex={0}
              autoFocus
            />
          </div>

          {/* Seletor de arquivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecione o arquivo *
            </label>
            <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 bg-gray-50">
              <div className="flex flex-col items-center">
                <Upload size={32} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {file ? file.name : 'Clique para selecionar ou arraste o arquivo'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  MP4, MOV, AVI, WebM, OGG, MP3, WAV - Máx 500MB
                </span>
              </div>
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".mp4,.mov,.avi,.webm,.ogg,.mp3,.wav,.m4a"
                disabled={uploading || transcribing}
                className="hidden"
              />
            </label>
          </div>

          {/* Status */}
          {uploading || transcribing ? (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="animate-spin">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
                <span className="text-sm font-medium text-blue-800">
                  {transcribing ? 'Transcrevendo vídeo...' : `Enviando... ${uploadProgress}%`}
                </span>
              </div>
              <p className="text-xs text-blue-700">
                {transcribing
                  ? 'Aguarde enquanto processamos a transcrição com Whisper. Isso pode levar alguns minutos.'
                  : 'Enviando em partes para garantir estabilidade...'}
              </p>
              {uploadProgress > 0 && (
                <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          ) : null}

          {/* Erro */}
          {error && (
            <div className="bg-red-50 p-4 rounded-lg flex items-start space-x-2">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}

          {/* Sucesso */}
          {transcriptionStatus === 'completed' && !transcribing && (
            <div className="bg-green-50 p-4 rounded-lg flex items-center space-x-2">
              <Check size={20} className="text-green-500" />
              <span className="text-sm text-green-800">Vídeo e transcrição prontos!</span>
            </div>
          )}
        </div>

        {/* Botões */}
        <div className="flex gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium disabled:opacity-50"
            disabled={uploading || transcribing}
          >
            Cancelar
          </button>
          <button
            onClick={handleUpload}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50"
            disabled={!file || !title.trim() || uploading || transcribing}
          >
            {uploading || transcribing ? 'Processando...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
